import { getValueForColumn } from '@/pages/reports/non-ats-reports/ats/constants/columnMapping';
import { createSlice } from '@reduxjs/toolkit';
import { formatAtsCandidate } from '../common/formatAtsCandidate';
import { lookupValue } from '../common/lookup';
import { fetchAtsReports, fetchAtsReportsDrillDown } from './actions';

const constructStages = (stageMapping) => {
  return Object.entries(stageMapping).map(([stage, name]) => ({
    name,
    key: `${stage}_total_count`,
  }));
};

const constructChart = (payload) => {
  const {
    misc: { stage_mapping = {} } = {},
    headers = [],
    chart = [],
  } = payload;
  const stages = constructStages(stage_mapping);

  if (!chart[0]) {
    return {};
  }

  return stages.map(({ name, key }) => {
    const valueIndex = headers.indexOf(key);
    return {
      name,
      value: chart[0]?.[valueIndex],
    };
  });
};

const constructStageColumnsWithMetrics = (stages) => {
  return stages
    .map(({ key, name }, inx) => {
      const columns = [{ key, name }];
      if (inx !== 0) {
        columns.unshift({
          name: '',
          key: `${key}_passthrough_rate`,
        });
        columns.unshift({
          name: '',
          key: `${key}_overall_rate`,
        });
      }
      if (inx !== stages.length - 1) {
        columns.push({
          name: '',
          key: `${key.replace('_total_count', '_time_in_stage')}`,
        });
      }
      return columns;
    })
    .reduce((acc, arr) => [...acc, ...arr], []);
};

const constructTable = (state, payload) => {
  const {
    misc: { stage_mapping = {} } = {},
    table = [],
    headers = [],
    lookups = {},
  } = payload;
  const { groupBy, breakdown } = state;

  const stages = constructStages(stage_mapping);

  const columns = [
    {
      key: 'g1',
      name: groupBy.name,
    },
    ...constructStageColumnsWithMetrics(stages),
  ];

  const data = table.reduce((acc, data) => {
    const obj = {};
    columns.forEach(({ key }, inx) => {
      const valueIndex = headers.indexOf(key);

      if (key === 'g1') {
        const lookedUpValue = lookupValue(lookups, key, data[valueIndex]);
        obj[key] = getValueForColumn(groupBy.id, lookedUpValue);
      } else if (lookups[key]) {
        obj[key] = lookupValue(lookups, key, data[valueIndex]);
      } else if (key.endsWith('_passthrough_rate')) {
        const previousStage = columns[inx - 3].key;
        const previousStageIndex = headers.indexOf(previousStage);
        const previous = data[previousStageIndex];

        const currentStage = columns[inx + 1].key;
        const currentStageIndex = headers.indexOf(currentStage);
        const current = data[currentStageIndex];

        // Instead of divide-by-zero, assuming 100% increase if previous is zero
        obj[key] = Math.floor((current / (previous ?? 1)) * 100);
      } else if (key.endsWith('_overall_rate')) {
        // INFO: Assuming 2nd column is always the first stage
        const firstStage = columns[1].key;
        const firstStageIndex = headers.indexOf(firstStage);
        const first = data[firstStageIndex];

        const currentStage = columns[inx + 2].key;
        const currentStageIndex = headers.indexOf(currentStage);
        const current = data[currentStageIndex];

        // Instead of divide-by-zero, assuming 100% increase if first is zero
        obj[key] = Math.floor((current / (first ?? 1)) * 100);
      } else if (key.endsWith('_time_in_stage')) {
        const actualCountIndex = headers.indexOf(
          key.replace('_time_in_stage', '_actual_count')
        );
        const actualCount = data[actualCountIndex];

        const latencyIndex = headers.indexOf(
          key.replace('_time_in_stage', '_latency')
        );
        const latency = data[latencyIndex];

        obj[key] = Math.floor(latency / actualCount);
      } else {
        obj[key] = data[valueIndex];
      }
    });

    if (state.breakdown?.id) {
      // const g2Index = headers.indexOf('g2');
      const g2RawData = data[0];

      obj['g2'] = g2RawData.reduce((acc, data) => {
        const obj = {};
        columns.forEach(({ key }, inx) => {
          const valueIndex = headers.indexOf(key);

          if (key === 'g1') {
            const lookedUpValue = lookupValue(
              lookups,
              'g2',
              data[headers.indexOf('g2')]
            );
            obj['g2'] = getValueForColumn(breakdown.id, lookedUpValue);
          } else if (lookups[key]) {
            obj[key] = lookupValue(lookups, key, data[valueIndex]);
          } else if (key.endsWith('_passthrough_rate')) {
            const previousStage = columns[inx - 3].key;
            const previousStageIndex = headers.indexOf(previousStage);
            const previous = data[previousStageIndex];

            const currentStage = columns[inx + 1].key;
            const currentStageIndex = headers.indexOf(currentStage);
            const current = data[currentStageIndex];

            // Instead of divide-by-zero, assuming 100% increase if previous is zero
            obj[key] = Math.floor((current / (previous ?? 1)) * 100);
          } else if (key.endsWith('_overall_rate')) {
            // INFO: Assuming 2nd column is always the first stage
            const firstStage = columns[1].key;
            const firstStageIndex = headers.indexOf(firstStage);
            const first = data[firstStageIndex];

            const currentStage = columns[inx + 2].key;
            const currentStageIndex = headers.indexOf(currentStage);
            const current = data[currentStageIndex];

            // Instead of divide-by-zero, assuming 100% increase if first is zero
            obj[key] = Math.floor((current / (first ?? 1)) * 100);
          } else if (key.endsWith('_time_in_stage')) {
            const actualCountIndex = headers.indexOf(
              key.replace('_time_in_stage', '_actual_count')
            );
            const actualCount = data[actualCountIndex];

            const latencyIndex = headers.indexOf(
              key.replace('_time_in_stage', '_latency')
            );
            const latency = data[latencyIndex];

            obj[key] = Math.floor(latency / actualCount);
          } else {
            obj[key] = data[valueIndex];
          }
        });
        acc.push(obj);
        return acc;
      }, []);
    }

    acc.push(obj);
    return acc;
  }, []);

  return {
    columns,
    data,
  };
};

const constructAtsData = (state, payload) => {
  const { filter_by_date } = payload;

  const chart = constructChart(payload);
  const { columns, data } = constructTable(state, payload);

  return {
    chart,
    columns,
    data,
    date: filter_by_date,
  };
};

const initialState = {
  isLoading: true,
  chart: [],
  columns: [],
  data: [],

  error: false,

  drillDown: {
    isLoading: true,
    data: [],
  },

  filterBy: {},
  groupBy: {
    name: 'Job Title',
    id: 'job',
  },
  breakdown: null,
  metric: {
    name: 'Passthrough Rate',
    id: 'passthrough_rate',
  },

  view: 'all',

  // Date responded back by the API,
  // to be displayed above funnel
  date: undefined,
};

const { reducer, actions } = createSlice({
  name: 'ats-reports',
  initialState,

  reducers: {
    setView: (state, { payload: view }) => {
      state.view = view;
    },
    setFilterBy: (state, { payload: filterBy }) => {
      state.filterBy = filterBy;
    },
    setGroupBy: (state, { payload: groupBy }) => {
      state.groupBy = groupBy;
    },
    setBreakdown: (state, { payload: breakdown }) => {
      state.breakdown = breakdown;
    },
    setMetric: (state, { payload: metric }) => {
      state.metric = metric;
    },

    clearAtsReportsDrillDown: (state) => {
      state.drillDown = initialState.drillDown;
    },
  },

  extraReducers: {
    [fetchAtsReports.pending]: (state) => {
      state.error = false;
      state.isLoading = true;
    },
    [fetchAtsReports.rejected]: (state) => {
      state.error = true;
      state.isLoading = false;
    },
    [fetchAtsReports.fulfilled]: (state, { payload = {} }) => {
      const { chart, columns, data, date } = constructAtsData(state, payload);
      state.chart = chart;
      state.columns = columns;
      state.data = data;
      state.date = date;
      state.isLoading = false;
    },
    [fetchAtsReportsDrillDown.pending]: (state) => {
      state.drillDown.isLoading = true;
    },
    [fetchAtsReportsDrillDown.fulfilled]: (state, { payload = {} }) => {
      const { table = [], headers = [], lookups = {} } = payload;

      const data = table
        .reduce((acc, data) => {
          const obj = {};
          headers.forEach((column, inx) => {
            const value = data[inx];
            if (lookups[column]?.[value]) {
              obj[column] = lookups[column]?.[value];
            } else {
              obj[column] = value;
            }
          });

          acc.push(obj);
          return acc;
        }, [])
        .map((datum) => {
          const { ats_candidate, contact } = datum?.k1 ?? {};
          return formatAtsCandidate(ats_candidate ?? {}, contact ?? {});
        });

      state.drillDown = {
        isLoading: false,
        data,
      };
    },
  },
});

export const {
  setView,
  setFilterBy,
  setGroupBy,
  setBreakdown,
  setMetric,
  clearAtsReportsDrillDown,
} = actions;

export default reducer;
